
import React, { useState, useEffect } from 'react';
import { ChartBar, Receipt } from '@phosphor-icons/react';
import { DashboardWidget, WidgetKopf, WidgetLaedt } from './DashboardWidget';
import { ladeSichtbareRechnungen } from '../../lib/dashboard/rechnungenSicht';
import { istMeinBeleg, type ScopeNutzer } from '../../lib/dashboard/scope';

interface MonatDaten {
  monat: string;
  label: string;
  bezahlt: number;
  offen: number;
}

interface RechnungenChartProps {
  onNavigate?: (path: string) => void;
  userProfile?: any;
  /** Mitarbeitende: nur Belege ihrer eigenen bzw. Team-Zuständigkeit zählen. */
  nurEigene?: boolean;
  nutzer?: ScopeNutzer;
}

const MONAT_KURZ = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

const RechnungenChart: React.FC<RechnungenChartProps> = ({ onNavigate, userProfile, nurEigene, nutzer }) => {
  const [data, setData] = useState<MonatDaten[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.id, nurEigene]);

  const fetchData = async () => {
    try {
      // Sichtbarkeitsgefiltert wie die Rechnungsliste (personen-/team-orientiert).
      // Nur `rechnungen` (kein KI-Pool) — wie zuvor.
      const { rechnungen: sichtbare } = await ladeSichtbareRechnungen(userProfile);

      if (!sichtbare) return;
      // Mitarbeitende: aus dem Lesbaren nur die eigenen bzw. Team-Belege.
      const rechnungen = nurEigene && nutzer
        ? sichtbare.filter((r) => istMeinBeleg(r, nutzer))
        : sichtbare;

      const now = new Date();
      const months: MonatDaten[] = [];

      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        months.push({
          monat: key,
          label: MONAT_KURZ[d.getMonth()],
          bezahlt: 0,
          offen: 0,
        });
      }

      rechnungen.forEach(r => {
        const rMonat = r.created_at?.substring(0, 7);
        const entry = months.find(m => m.monat === rMonat);
        if (entry) {
          const betrag = Math.abs(Number(r.brutto) || 0);
          if (r.status === 'bezahlt') {
            entry.bezahlt += betrag;
          } else {
            entry.offen += betrag;
          }
        }
      });

      setData(months);
    } catch (err) {
      console.error('Error fetching rechnungen chart:', err);
    } finally {
      setLoading(false);
    }
  };

  const maxVal = Math.max(...data.map(d => d.bezahlt + d.offen), 1);
  const barMaxHeight = 74;

  const gesamtBezahlt = data.reduce((s, d) => s + d.bezahlt, 0);
  const gesamtOffen = data.reduce((s, d) => s + d.offen, 0);
  const euro = (v: number) => `${v.toLocaleString('de-DE', { maximumFractionDigits: 0 })} €`;

  return (
    <DashboardWidget farbe="--dw-orange" icon={ChartBar} ariaLabel="Rechnungen"
      onClick={() => onNavigate?.('/admin/rechnungen')} tip="Rechnungen – Zur Rechnungsliste">
      <WidgetKopf farbe="--dw-orange" icon={Receipt} titel="Rechnungen" untertitel="Letzte 6 Monate" />

      {loading ? (
        <WidgetLaedt />
      ) : (
        <>
          {/* Summary */}
          <div className="flex flex-wrap" style={{ gap: 16 }}>
            {([['Bezahlt', 'var(--ok)', gesamtBezahlt], ['Offen', 'var(--warn)', gesamtOffen]] as const).map(([label, farbe, wert]) => (
              <span key={label} className="flex items-center" style={{ gap: 6, fontSize: 12.5, fontWeight: 600, color: 'var(--ink2)' }}>
                <span style={{ width: 9, height: 9, borderRadius: 3, background: farbe }} />
                {label}
                <strong className="whitespace-nowrap tabular-nums" style={{ color: 'var(--ink)' }}>{euro(wert)}</strong>
              </span>
            ))}
          </div>

          {/* Bar Chart */}
          <div className="flex items-end" style={{ height: barMaxHeight + 44, gap: 10, paddingTop: 24 }}>
            {data.map((d, i) => {
              const bezahltH = (d.bezahlt / maxVal) * barMaxHeight;
              const offenH = (d.offen / maxVal) * barMaxHeight;
              const isHovered = hoveredIndex === i;
              const totalMonat = d.bezahlt + d.offen;

              return (
                <div
                  key={d.monat}
                  className="relative flex h-full flex-1 flex-col items-center justify-end"
                  style={{ gap: 6 }}
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  {/* Tooltip */}
                  {isHovered && totalMonat > 0 && (
                    <span className="absolute z-10 flex items-center whitespace-nowrap"
                      style={{ top: -22, height: 22, padding: '0 8px', borderRadius: 11, fontSize: 11, fontWeight: 800,
                        background: 'var(--tip-bg)', color: 'var(--tip-ink)', boxShadow: '0 6px 16px -6px rgba(0,0,0,0.35)' }}>
                      {euro(totalMonat)}
                    </span>
                  )}

                  {/* Stacked Bars */}
                  <div className="flex w-full flex-col" style={{ maxWidth: 30, gap: 2, transition: 'opacity .2s', opacity: hoveredIndex === null || isHovered ? 1 : 0.55 }}>
                    {offenH > 0 && <span style={{ height: offenH, borderRadius: '6px 6px 2px 2px', background: 'var(--warn)' }} />}
                    {bezahltH > 0 && <span style={{ height: bezahltH, borderRadius: offenH > 0 ? '2px 2px 6px 6px' : 6, background: 'var(--ok)' }} />}
                    {totalMonat === 0 && <span style={{ height: 4, borderRadius: 3, background: 'var(--fill2)' }} />}
                  </div>

                  {/* Label */}
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: isHovered ? 'var(--acc)' : 'var(--ink3)' }}>{d.label}</span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </DashboardWidget>
  );
};

export default RechnungenChart;
